/**
 * FileUpload Component (UploadFile)
 * Handles image uploads from camera or gallery with editing capability
 * Processes images for optimization and provides drawer-based UI
 * @component
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import Drawer from '@mui/material/Drawer';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import CollectionsOutlinedIcon from '@mui/icons-material/CollectionsOutlined';
import { makeStyles } from '@mui/styles';
import { translate } from '../../../helper';
import docUpload from '../../../assets/image/svg/docUpload.svg';
import Editor from '../../components/editor/index';
import Utils from '../../../utils';
import { processImageFile } from '../../../utils/imageProcessor';
import Webcam from 'react-webcam';
import { useRef } from 'react';
import CustomSpinner from '../../../common/CustomSpinner';
import { resolveCssVarValue } from '../../../utils/CommonFunction';
import FlipCameraIosIcon from '@mui/icons-material/FlipCameraIos';

// Upload box container styles
const uploadBoxStyle = {
  width: '100%',
  height: '120px',
  marginTop: '15px',
  border: '2px dashed #D8D8D8',
  borderRadius: '0.5rem',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  cursor: 'pointer'
};

// Content area styling for icon and text
const contentStyle = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  color: '#828282',
  cursor: 'pointer'
};

// Label text styling
const labelStyle = {
  color: '#828282'
};

// MUI custom styles for drawer
const useStyles = makeStyles((theme) => ({
  drawerPaper: {
    borderRadius: 5,
    backgroundColor: '#fff'
  }
}));

// Maximum file size in bytes (10 MB)
const MAX_FILE_SIZE = 10 * 1024 * 1024;
// Maximum number of photos allowed
const MAX_PHOTOS = 5;

/**
 * FileUpload Component - Image upload with camera/gallery and editor
 * @param {Object} props
 * @param {Function} props.onChange - Callback when files are uploaded/processed
 * @param {string} [props.label] - Label text above upload box
 * @param {Function} [props.onCamUpload] - Callback when camera image is edited and saved
 * @param {Array} [props.files] - Current list of uploaded files
 * @param {Function} [props.onProcessingChange] - Callback for processing state changes
 * @returns {JSX.Element} File upload UI with drawer options and editor
 */
function FileUpload(props) {
  const { onChange, label, onCamUpload, files, onProcessingChange } = props;

  const classes = useStyles();

  // UI state management
  const [open, setOpen] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [imgFile, setImageFile] = useState(null);

  // Batch upload tracking
  const [uploadedFiles, setUploadedFiles] = useState(0);
  const [uploadedFilesUrl, setUploadedFilesUrl] = useState([]);
  const webcamRef = useRef(null);
  const cameraFrameRef = useRef(null);
  const [useWebCamera, setUseWebCamera] = useState(false);
  const [showWebcam, setShowWebcam] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [facingMode, setFacingMode] = useState('environment');
  const [isSwitching, setIsSwitching] = useState(false);
  useEffect(() => {
    if (navigator.deviceMemory && navigator.deviceMemory <= 4) {
      setUseWebCamera(true);
    }
  }, []);

  /**
   * Handles single file upload (camera or gallery single)
   * Validates file size, processes image, and opens editor
   * @param {Event} e - Input change event
   */
  const onUpload = useCallback(
    async (e) => {
      onProcessingChange?.(true);
      const file = e.target.files?.[0];

      if (!file) {
        onProcessingChange?.(false);
        return;
      }

      // Validate file size (10 MB limit)
      if (file.size > MAX_FILE_SIZE) {
        Utils.showAlert(2, 'Upload image less than 10MB');
        onProcessingChange?.(false);
        return;
      }

      try {
        // Process image for optimization (compression, format conversion)
        const processed = await processImageFile(file);
        setImageFile(processed || file);
      } catch (err) {
        Utils.showAlert(2, 'Error processing image.');
        onProcessingChange?.(false);
        return;
      }

      // Open editor drawer
      setOpen(false);
      setEditModal(true);
      onProcessingChange?.(false);
    },
    [onProcessingChange]
  );

  const captureFromWebcam = async () => {
    const video = webcamRef.current.video;
    const frame = cameraFrameRef.current;

    if (!video || !frame) return;

    const videoWidth = video.videoWidth;
    const videoHeight = video.videoHeight;

    const frameRect = frame.getBoundingClientRect();
    const frameWidth = frameRect.width;
    const frameHeight = frameRect.height;

    const videoAspect = videoWidth / videoHeight;
    const frameAspect = frameWidth / frameHeight;

    let renderedWidth,
      renderedHeight,
      offsetX = 0,
      offsetY = 0;

    if (videoAspect > frameAspect) {
      renderedHeight = frameHeight;
      renderedWidth = videoAspect * renderedHeight;
      offsetX = (renderedWidth - frameWidth) / 2;
    } else {
      renderedWidth = frameWidth;
      renderedHeight = renderedWidth / videoAspect;
      offsetY = (renderedHeight - frameHeight) / 2;
    }

    const scaleX = videoWidth / renderedWidth;
    const scaleY = videoHeight / renderedHeight;

    // 🔥 APPLY ZOOM HERE
    const zoomFactor = zoom; // 0.6, 1, 2, 3

    const cropWidth = (frameWidth * scaleX) / zoomFactor;
    const cropHeight = (frameHeight * scaleY) / zoomFactor;

    const sx = offsetX * scaleX + (frameWidth * scaleX - cropWidth) / 2;
    const sy = offsetY * scaleY + (frameHeight * scaleY - cropHeight) / 2;

    const canvas = document.createElement('canvas');
    canvas.width = cropWidth;
    canvas.height = cropHeight;

    const ctx = canvas.getContext('2d');
    if (facingMode === 'user') {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }

    ctx.drawImage(video, sx, sy, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);

    const imageSrc = canvas.toDataURL('image/jpeg', 1);

    try {
      const res = await fetch(imageSrc);
      const blob = await res.blob();

      const file = new File([blob], 'camera.jpg', {
        type: 'image/jpeg'
      });

      onUpload({
        target: { files: [file] }
      });

      setShowWebcam(false);
    } catch (err) {
      console.error(err);
    }
  };

  /**
   * Toggles main drawer (camera/gallery selection)
   */
  const handleDrawer = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  /**
   * Closes editor drawer
   */
  const handleCloseEditDrawer = useCallback(() => {
    setEditModal(false);
  }, []);

  /**
   * Handles camera capture
   * Validates max photo count and processes captured image
   * @param {Event} e - Input change event from camera
   */
  const onCameraCapture = useCallback(
    (e) => {
      setOpen(false);

      // Check if adding new photo exceeds limit
      if ((files?.length || 0) >= MAX_PHOTOS || (e.target.files?.length || 0) > MAX_PHOTOS) {
        Utils.showAlert(2, 'Upload maximum 5 photos');
        return;
      }

      onUpload(e);
    },
    [files?.length, onUpload]
  );

  /**
   * Handles multiple file selection from gallery
   * Validates file count and size, processes all images
   * @param {Event} e - Input change event from gallery
   */
  const onGalleryUpdate = useCallback(
    async (e) => {
      onProcessingChange?.(true);
      setOpen(false);

      const filesArray = Array.from(e.target.files || []);
      const totalPhotos = (files?.length || 0) + filesArray.length;

      // Validate total photo count
      if ((files?.length || 0) >= MAX_PHOTOS || filesArray.length > MAX_PHOTOS) {
        Utils.showAlert(2, 'Upload maximum 5 photos');
        onProcessingChange?.(false);
        return;
      }

      setUploadedFiles(filesArray.length);
      const processedFiles = [];

      // Process each selected file
      for (let file of filesArray) {
        // Validate file size
        if (file.size > MAX_FILE_SIZE) {
          Utils.showAlert(2, 'Upload image less than 10MB');
          continue;
        }

        try {
          // Process image for optimization
          const processed = await processImageFile(file);
          processedFiles.push(processed || file);
        } catch (err) {
          Utils.showAlert(2, 'Error processing image.');
          continue;
        }
      }

      // Update processed files list
      setUploadedFilesUrl((prev) => [...prev, ...processedFiles]);
      onProcessingChange?.(false);
    },
    [files?.length, onProcessingChange]
  );

  /**
   * Triggers onChange callback when all files are processed
   * Only fires when count of processed files matches uploaded count
   */
  useEffect(() => {
    if (uploadedFilesUrl.length === uploadedFiles && uploadedFilesUrl.length !== 0) {
      onChange({ target: { files: uploadedFilesUrl } });
    }
  }, [uploadedFilesUrl, uploadedFiles, onChange]);

  /**
   * Converts data URL string to File object
   * Used to convert canvas drawing data to uploadable file
   * @param {string} dataurl - Data URL from canvas (data:image/png;base64,...)
   * @param {string} filename - Output filename
   * @returns {File} Converted file object
   * @throws {Error} If data URL format is invalid
   */
  const dataURLtoFile = useCallback((dataurl, filename) => {
    if (!dataurl || typeof dataurl !== 'string' || !dataurl.includes(',')) {
      throw new Error('Invalid data URL provided to dataURLtoFile.');
    }

    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    // Convert base64 string to byte array
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
  }, []);

  /**
   * Saves edited image from editor and passes to parent
   * @param {string} data - Data URL from canvas editor
   */
  const save = useCallback(
    (data) => {
      try {
        const file = dataURLtoFile(data, 'edited_image.jpg');
        onCamUpload?.(file);
      } catch (err) {
        Utils.showAlert(2, 'Error saving edited image.');
      }
    },
    [dataURLtoFile, onCamUpload]
  );

  // Memoize drawer styles to prevent unnecessary re-renders
  const editorDrawerStyle = useMemo(
    () => ({
      backgroundColor: '#0f1924'
    }),
    []
  );

  const editorDrawerPaperProps = useMemo(
    () => ({
      sx: {
        backgroundColor: '#0f1924',
        height: '100dvh'
      }
    }),
    []
  );

  const toggleCamera = () => {
    setIsSwitching(true);

    setFacingMode((prev) => (prev === 'environment' ? 'user' : 'environment'));
  };

  return (
    <>
      {/* Optional label above upload box */}
      {label && label.length > 0 && (
        <div style={labelStyle} className="text-input">
          {label}
        </div>
      )}

      {/* Upload trigger area */}
      <form onClick={handleDrawer} style={uploadBoxStyle}>
        <label style={contentStyle} htmlFor="capture">
          <img
            src={docUpload}
            style={{ width: '30px', marginBottom: '10px', cursor: 'pointer' }}
            alt="docUpload"
          />
          <span>
            {translate('addCameraImage')} <br /> Or {translate('uploadStorage')}
          </span>
        </label>
      </form>

      {/* Camera/Gallery selection drawer */}
      <Drawer anchor={'bottom'} open={open} onClose={handleDrawer} className={classes.drawerPaper}>
        <div
          style={{
            padding: '50px 20px',
            display: 'flex',
            flexDirection: 'column'
          }}>
          {/* Camera option */}
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div
              onClick={() => {
                if (useWebCamera) {
                  setOpen(false);
                  setShowWebcam(true);
                } else {
                  document.getElementById('captureimage')?.click(); // manual trigger
                }
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '20px',
                cursor: 'pointer'
              }}>
              <CameraAltOutlinedIcon style={{ marginRight: '10px' }} />
              <span>Camera</span>
            </div>
          </div>
          <input
            type="file"
            accept="image/*"
            capture="environment"
            id="captureimage"
            onChange={onCameraCapture}
            style={{ display: 'none' }}
          />

          {/* Gallery option */}
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <label
              htmlFor="galleryInput"
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '50px',
                cursor: 'pointer'
              }}>
              <CollectionsOutlinedIcon style={{ marginRight: '10px' }} />
              <span>Gallery</span>
            </label>
          </div>
          <input
            type="file"
            accept="image/*"
            id="galleryInput"
            multiple={true}
            onChange={onGalleryUpdate}
            style={{ display: 'none' }}
          />

          {/* Cancel button */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: '500',
              cursor: 'pointer'
            }}
            onClick={handleDrawer}
            role="button"
            tabIndex={0}>
            Cancel
          </div>
        </div>
      </Drawer>
      {showWebcam && (
        <Drawer
          anchor="bottom"
          open={showWebcam}
          onClose={() => setShowWebcam(false)}
          PaperProps={{
            sx: {
              height: '100dvh', // ✅ FULL HEIGHT
              background: '#000'
            }
          }}>
          <div
            style={{
              width: '100%',
              height: '100%',
              position: 'relative',
              background: '#000'
            }}>
            {/* 🔝 TOP UI */}
            <div
              style={{
                position: 'absolute',
                top: 0,
                width: '100%',
                height: '10%',
                background: '#000',
                zIndex: 20,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px 16px',
                color: '#fff'
              }}>
              <span>⚡</span>
              <span
                style={{
                  color: '#fff',
                  fontWeight: '600',
                  fontSize: '14px',
                  letterSpacing: '1px'
                }}>
                PHOTO
              </span>
              <span>😊</span>
            </div>

            {/* 🎥 CAMERA VIEW AREA */}
            <div
              ref={cameraFrameRef}
              style={{
                position: 'absolute',
                top: '10%',
                width: '100%',
                height: '65%',
                overflow: 'hidden',
                background: '#000',
                transform: `scale(${zoom})`, // ✅ correct zoom feel
                transformOrigin: 'center'
              }}>
              {isSwitching && (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    background: 'rgba(0,0,0,0.4)',
                    backdropFilter: 'blur(8px)',
                    zIndex: 25,
                    display: 'flex',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    alignItems: 'center',
                    color: '#fff'
                  }}>
                  <CustomSpinner size={resolveCssVarValue('--fs-hero')} />
                  <br />
                  Switching...
                </div>
              )}
              <Webcam
                key={facingMode}
                ref={webcamRef}
                onUserMedia={() => setIsSwitching(false)}
                screenshotFormat="image/jpeg"
                screenshotQuality={1}
                videoConstraints={{
                  facingMode,
                  width: { ideal: 1920 },
                  height: { ideal: 1080 }
                }}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  transform: facingMode === 'user' ? 'scaleX(-1)' : 'none'
                }}
              />
            </div>

            {/* 🔽 BOTTOM UI */}
            <div
              style={{
                position: 'absolute',
                bottom: 0,
                width: '100%',
                paddingBottom: 'calc(env(safe-area-inset-bottom) + 20px)',
                paddingTop: '16px',
                background:
                  'linear-gradient(to top, rgba(0,0,0,0.9), rgba(0,0,0,0.4), transparent)',
                backdropFilter: 'blur(20px)',
                zIndex: 20,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}>
              {/* 🔍 Zoom Pills */}
              <div
                style={{
                  display: 'flex',
                  gap: '10px',
                  padding: '6px 12px',
                  borderRadius: '30px',
                  background: 'rgba(255,255,255,0.08)',
                  backdropFilter: 'blur(10px)',
                  marginBottom: '18px'
                }}>
                {[1, , 1.5, 2, 2.5, 3].map((z) => (
                  <button
                    key={z}
                    onClick={() => setZoom(z)}
                    style={{
                      padding: '6px 14px',
                      borderRadius: '20px',
                      border: 'none',
                      background: zoom === z ? '#fff' : 'transparent',
                      color: zoom === z ? '#000' : '#fff',
                      fontWeight: '600',
                      fontSize: '13px',
                      transition: 'all 0.2s ease'
                    }}>
                    {z}x
                  </button>
                ))}
              </div>

              {/* 🎯 Bottom Controls */}
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '0 24px'
                }}>
                {/* Cancel */}
                <button
                  onClick={() => setShowWebcam(false)}
                  style={{
                    color: '#aaa',
                    background: 'transparent',
                    border: 'none',
                    fontSize: '15px'
                  }}>
                  Cancel
                </button>

                {/* 📸 Shutter (Premium) */}
                <div
                  onClick={captureFromWebcam}
                  style={{
                    width: 84,
                    height: 84,
                    borderRadius: '50%',
                    border: '3px solid rgba(255,255,255,0.9)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    boxShadow: '0 0 20px rgba(255,255,255,0.3)',
                    background: 'rgba(255,255,255,0.05)',
                    backdropFilter: 'blur(6px)',
                    transition: 'transform 0.1s'
                  }}>
                  <div
                    style={{
                      width: 64,
                      height: 64,
                      borderRadius: '50%',
                      background: '#fff'
                    }}
                  />
                </div>

                {/* 🔄 Flip */}
                <button
                  onClick={toggleCamera}
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.1)',
                    border: 'none',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}>
                  <FlipCameraIosIcon style={{ fontSize: 22, color: '#fff' }} />
                </button>
              </div>
            </div>
          </div>
        </Drawer>
      )}

      {/* Image editor drawer */}
      <Drawer
        anchor={'bottom'}
        open={editModal}
        onClose={handleCloseEditDrawer}
        className={classes.drawerPaper}
        style={editorDrawerStyle}
        PaperProps={editorDrawerPaperProps}>
        <div
          style={{
            height: '100%',
            display: 'flex',
            backgroundColor: '#0f1924',
            flexDirection: 'column',
            padding: '15px 10px'
          }}>
          <Editor onClose={handleCloseEditDrawer} onSave={save} imageUrl={imgFile} />
        </div>
      </Drawer>
    </>
  );
}

FileUpload.displayName = 'FileUpload';

export default FileUpload;
